import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "src/common/state/hooks";
import ModalStandard from "../../../common/components/Modal/ModalStandard";
import { copyToClipboard } from "../../../common/utils/clipboard";
import { getMfaRecoveryCodes } from "../state/mfaSlice";

interface RecoveryCodesDialogProps {
  isOpen: boolean;
  /** Only reachable via the "I have saved these" confirmation — see Anti-Goals
   * in Phase 6: the backend returns these codes exactly once, so this dialog
   * must not be dismissible (backdrop click, Escape) without it. */
  onClose: () => void;
}

/**
 * Shown once right after a successful enrollment or a recovery-code
 * regeneration (docs/plans/todo/creator-mfa-two-factor-auth.md, Phase 6 step 5).
 * Codes live only in `state.mfa.recoveryCodes` for the dialog's lifetime —
 * `onClose` is responsible for clearing them (see MfaSection.tsx).
 */
const RecoveryCodesDialog = ({ isOpen, onClose }: RecoveryCodesDialogProps) => {
  const { t } = useTranslation();
  const codes = useAppSelector(getMfaRecoveryCodes);

  const [hasConfirmedSaved, setHasConfirmedSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  // Reset the local confirmation checkbox whenever a fresh batch of codes is
  // shown (enrollment, then later a regeneration) rather than only on mount.
  useEffect(() => {
    setHasConfirmedSaved(false);
    setCopied(false);
  }, [codes]);

  const codesText = (codes ?? []).join("\n");

  const handleCopy = async () => {
    if (!codes || codes.length === 0) return;
    await copyToClipboard(codesText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!codes || codes.length === 0) return;
    const blob = new Blob([`${codesText}\n`], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "wixar-recovery-codes.txt");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <ModalStandard
      id="modalMfaRecoveryCodes"
      fullScreen={false}
      controlledMode={{ isOpen }}
      clickOnBackgroundClose={false}
    >
      <div className="flex w-full">
        <div className="relative grow w-full max-w-7xl px-4 xl:px-0 mx-auto">
          <h2 className="pt-3 pb-1 font-medium text-lg">
            {t("pages.profile.mfa.recoveryCodes.title")}
          </h2>
          <p className="pb-3 text-red-700 font-medium">
            {t("pages.profile.mfa.recoveryCodes.shownOnceWarning")}
          </p>

          <div className="grid grid-cols-2 gap-2 bg-white rounded-lg border border-gray-300 p-4 font-mono text-sm">
            {(codes ?? []).map((code) => (
              <span key={code} className="select-all">
                {code}
              </span>
            ))}
          </div>

          <div className="flex gap-2 mt-3">
            <button type="button" className="btn-alternative-outline" onClick={handleCopy}>
              {copied ? t("pages.profile.mfa.recoveryCodes.copied") : t("general.copy")}
            </button>
            <button type="button" className="btn-alternative-outline" onClick={handleDownload}>
              {t("general.download")}
            </button>
          </div>

          <label className="flex items-center gap-2 mt-4 cursor-pointer">
            <input
              type="checkbox"
              className="rounded border-gray-300 text-indigo-600 shadow-sm focus:ring-indigo-500"
              checked={hasConfirmedSaved}
              onChange={(e) => setHasConfirmedSaved(e.target.checked)}
            />
            <span className="text-sm">{t("pages.profile.mfa.recoveryCodes.confirmCheckbox")}</span>
          </label>

          <div className="flex justify-end mt-3">
            <button
              type="button"
              className="btn-primary-fill"
              disabled={!hasConfirmedSaved}
              onClick={onClose}
            >
              {t("general.close")}
            </button>
          </div>
        </div>
      </div>
    </ModalStandard>
  );
};

export default RecoveryCodesDialog;
